import { GetUserType } from '@orbit/api-types'

export type SplitType = 'Equally' | 'Value' | 'Percent'

export interface SplitParticipant {
	userId: number
	splitAmount: number | null
}

export interface SplitValidation {
	valid: boolean
	message: string
}

/** Decimal-string amount (e.g. "12.50") to integer cents. */
export const toCents = (amount: string): number => Math.round(parseFloat(amount || '0') * 100)

/** Integer cents to a 2-decimal string (e.g. 1250 -> "12.50"). */
export const fromCents = (cents: number): string => (cents / 100).toFixed(2)

/** Returns members sorted so the current user (if present) is first. */
export const sortMembersWithCurrentFirst = (
	members: GetUserType[],
	currentUserId: number | undefined,
): GetUserType[] =>
	[...members].sort((a, b) => {
		if (a.id === currentUserId) return -1
		if (b.id === currentUserId) return 1
		return 0
	})

export const getInitials = (member: GetUserType): string => {
	const first = member.firstName?.[0] ?? ''
	const last = member.lastName?.[0] ?? ''
	return (first + last).toUpperCase() || '?'
}

export const getMemberName = (member: GetUserType, currentUserId: number | undefined): string => {
	const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ')
	return member.id === currentUserId ? `${fullName} (You)` : fullName
}

/**
 * Builds the participants payload from the selected ids and per-user split
 * amounts. Equal splits carry a null amount; Percent and Value carry integer
 * cents (percent * 100 / value * 100).
 */
export const getParticipantsPayload = (
	selectedParticipants: Set<number>,
	splitType: SplitType,
	splitAmounts: Record<number, string>,
): SplitParticipant[] => {
	const participantIds = Array.from(selectedParticipants)
	if (splitType === 'Equally') {
		return participantIds.map(userId => ({ userId, splitAmount: null }))
	}
	// Percent and Value both store integer hundredths.
	return participantIds.map(userId => ({
		userId,
		splitAmount: Math.round(parseFloat(splitAmounts[userId] || '0') * 100),
	}))
}

/**
 * Single source of truth for split validation across Add/Edit Expense.
 * Messages are identical to the previous inline implementations.
 */
export const getSplitValidation = (
	selectedParticipants: Set<number>,
	splitType: SplitType,
	splitAmounts: Record<number, string>,
	amount: string,
	symbol: string,
): SplitValidation => {
	if (selectedParticipants.size === 0) {
		return { valid: false, message: 'Select at least one participant' }
	}
	if (splitType === 'Equally') {
		return { valid: true, message: `Split evenly between ${selectedParticipants.size} people` }
	}
	if (splitType === 'Percent') {
		const total = Array.from(selectedParticipants).reduce(
			(sum, id) => sum + parseFloat(splitAmounts[id] || '0'),
			0,
		)
		if (Math.abs(total - 100) > 0.01) {
			return { valid: false, message: `Percentages total ${total.toFixed(1)}% (must be 100%)` }
		}
		return { valid: true, message: '100% allocated' }
	}
	// Value
	const total = Array.from(selectedParticipants).reduce(
		(sum, id) => sum + parseFloat(splitAmounts[id] || '0'),
		0,
	)
	const expenseAmount = parseFloat(amount || '0')
	if (Math.abs(total - expenseAmount) > 0.01) {
		return {
			valid: false,
			message: `${symbol}${total.toFixed(2)} of ${symbol}${expenseAmount.toFixed(2)} allocated`,
		}
	}
	return { valid: true, message: `${symbol}${total.toFixed(2)} fully allocated` }
}
